import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { FadeIn, Overline } from "../components/Motion";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/2f486ce3-6005-42d6-adf3-873498688899/images/85463882df0f2adbdf988342f21677624d27b74f6e3358c787aab73d6c82ca06.png";
const CONSTELLATION = "https://static.prod-images.emergentagent.com/jobs/2f486ce3-6005-42d6-adf3-873498688899/images/3d1d4da3194d1f57c604b8bee25de9e9c692d4933000bb3055848ff5909e557f.png";
const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/2f486ce3-6005-42d6-adf3-873498688899/images/706d1f5930a14eaf72024cc3c84e94282c656e2f638e2c6b1ffb9ef4ab172c5e.png";

const SERVICES = [
    { num: "01", title: "Business Development", body: "Open enterprise and strategic doors. Structure partnerships. Negotiate and close revenue-generating agreements." },
    { num: "02", title: "Distribution Strategy", body: "Identify scalable revenue channels. Activate multi-channel distribution across CTV, OLV, social and commerce." },
    { num: "03", title: "Monetization Frameworks", body: "Pricing and packaging. Sponsorship and media deal structuring. Performance-based revenue models." },
    { num: "04", title: "Operator Support", body: "Interim Head of BD / CRO layer. Fundraising narrative. Repeatable systems for scale — without payroll." },
];

const Home = () => {
    return (
        <main data-testid="page-home" className="text-white">
            {/* HERO */}
            <section className="relative min-h-[100vh] flex items-end pb-20 lg:pb-32 overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-[0.35]"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, rgba(5,7,10,0.55) 0%, rgba(5,7,10,0.6) 40%, rgba(5,7,10,0.95) 90%)" }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 starfield opacity-30 fade-edges pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32">
                    <FadeIn delay={0.1}>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-[7.5rem] leading-[0.95] tracking-tight text-white max-w-6xl">
                            Revenue. Distribution. <br />
                            <span className="italic text-accent">Without the overhead.</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="mt-10 max-w-3xl text-[16px] lg:text-xl leading-relaxed text-white/70">
                            We close partnerships, structure monetization, and unlock distribution.
                            Operator-grade execution. <span className="text-accent">No training wheels.</span>
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.3}>
                        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-contact"
                                className="group inline-flex items-center gap-3 border border-white/25 text-white px-7 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                            >
                                Book a conversation
                                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-cta-services"
                                className="hover-line text-[12px] tracking-[0.26em] uppercase text-white/65 hover:text-white"
                            >
                                Explore the model
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* POSITIONING */}
            <section className="relative py-32 lg:py-44">
                <div
                    className="absolute right-0 top-0 w-[55%] h-full bg-no-repeat bg-right bg-contain opacity-[0.18]"
                    style={{ backgroundImage: `url(${CONSTELLATION})` }}
                    aria-hidden="true"
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <FadeIn>
                            <Overline>The gap we close</Overline>
                        </FadeIn>
                    </div>
                    <div className="md:col-span-8">
                        <FadeIn delay={0.05}>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4.25rem] leading-[1.05] text-white">
                                Founders need senior operators — not strategy decks,
                                and not another full-time hire they can't justify.
                            </h2>
                        </FadeIn>
                        <FadeIn delay={0.15}>
                            <p className="mt-10 max-w-2xl text-[15px] lg:text-lg leading-relaxed text-white/65">
                                We function as a senior, execution-focused business development arm — on demand.
                                Not intros. Not advisory. <span className="text-white">Closed business.</span>
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-end justify-between gap-8 mb-16">
                        <FadeIn>
                            <Overline className="mb-4">What we actually do</Overline>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-3xl">
                                Four practices. <span className="italic text-accent">Built to move together.</span>
                            </h2>
                        </FadeIn>
                        <Link
                            to="/services"
                            data-testid="services-link"
                            className="hidden md:inline-flex items-center gap-2 text-[12px] tracking-[0.26em] uppercase text-white/60 hover:text-white hover-line"
                        >
                            All practices <ArrowRight size={14} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06]">
                        {SERVICES.map((s, i) => (
                            <FadeIn key={s.num} delay={i * 0.07} className="bg-[#05070A]">
                                <article
                                    data-testid={`service-card-${s.num}`}
                                    className="group p-10 lg:p-14 h-full flex flex-col justify-between min-h-[280px] transition-colors hover:bg-[#0A0E15]"
                                >
                                    <div className="flex items-start justify-between mb-12">
                                        <span className="font-display text-2xl text-white/30">{s.num}</span>
                                        <ArrowUpRight size={18} className="text-white/40 group-hover:text-white transition-colors" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-3xl lg:text-4xl leading-tight text-white mb-4">{s.title}</h3>
                                        <p className="text-[14px] lg:text-[15px] leading-relaxed text-white/60 max-w-md">{s.body}</p>
                                    </div>
                                </article>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY IT WINS — compare */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-[0.07]"
                    style={{ backgroundImage: `url(${TEXTURE})` }}
                    aria-hidden="true"
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <FadeIn>
                        <Overline className="mb-4">Why it wins</Overline>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-3xl">
                            Faster revenue. Lower burn. <span className="italic text-accent">A cleaner cap-table story.</span>
                        </h2>
                    </FadeIn>

                    <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06]">
                        <FadeIn className="bg-[#05070A]">
                            <div className="p-10 lg:p-14 h-full">
                                <Overline className="mb-8 text-white/40">Traditional path</Overline>
                                <ul className="space-y-5 text-white/55 text-[15px] leading-relaxed">
                                    <li className="flex gap-4"><span className="font-display text-white/30">—</span> Hire a BD team</li>
                                    <li className="flex gap-4"><span className="font-display text-white/30">—</span> Burn cash on salaries &amp; ramp</li>
                                    <li className="flex gap-4"><span className="font-display text-white/30">—</span> Slow time-to-revenue</li>
                                    <li className="flex gap-4"><span className="font-display text-white/30">—</span> Uncertain ROI</li>
                                </ul>
                            </div>
                        </FadeIn>
                        <FadeIn delay={0.1} className="bg-[#0A0E15]">
                            <div className="p-10 lg:p-14 h-full">
                                <Overline className="mb-8 text-white">Orion Road model</Overline>
                                <ul className="space-y-5 text-white text-[15px] leading-relaxed">
                                    <li className="flex gap-4"><span className="font-display text-accent">+</span> Plug in instantly</li>
                                    <li className="flex gap-4"><span className="font-display text-accent">+</span> Leverage existing network &amp; infrastructure</li>
                                    <li className="flex gap-4"><span className="font-display text-accent">+</span> Drive revenue quickly</li>
                                    <li className="flex gap-4"><span className="font-display text-accent">+</span> Scale without fixed cost</li>
                                </ul>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
                    <div className="md:col-span-8">
                        <FadeIn>
                            <Overline className="mb-6">Engagements</Overline>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4.5rem] leading-[1.05] text-white">
                                If we don't drive results, <span className="italic text-accent">we don't get paid meaningfully.</span>
                            </h2>
                        </FadeIn>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/how-we-work"
                            data-testid="cta-how-we-work"
                            className="inline-flex items-center gap-3 border border-white/25 text-white px-7 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                        >
                            How we work
                            <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Home;
